import React, { useMemo } from "react";
import { MockedProvider } from "@apollo/client/testing/react";
import type { Meta, StoryObj } from "@storybook/react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { InvitationTarget, ProfileType } from "../../__generated__/graphql";
import { buildClientProfile, buildInvitation, buildUser } from "../factories";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { ACCEPT_INVITATION, GET_INVITATION, REJECT_INVITATION } from "../../graphql/invitation";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import { StoryStackNavigatorContainer } from "../display-helpers";
import InvitationScreen from "../../screens/Invitation";

const Stack = createNativeStackNavigator<{
    Invitation: { invitationId: "id" };
}>();

type StoryProps = {
    /** `Worker`: invited to collaborate. `Client`: invited to join a colleague's company. */
    target: InvitationTarget;
};

const StoryComponent = ({ target }: StoryProps) => {
    const mocks = [
        {
            request: {
                query: GET_INVITATION,
                variables: () => true,
            },
            result: () => {
                return {
                    data: {
                        getInvitation: buildInvitation({ target }),
                    },
                };
            },
        },
        {
            request: {
                query: ACCEPT_INVITATION,
                variables: () => true,
            },
            result: () => {
                return {
                    data: {},
                };
            },
        },
        {
            request: {
                query: REJECT_INVITATION,
                variables: () => true,
            },
            result: () => {
                return {
                    data: {},
                };
            },
        },
    ];
    const user = useMemo(
        () =>
            buildUser({
                clientProfiles: [buildClientProfile()],
            }),
        [],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Client}>
                <MockedProvider mocks={mocks}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="Invitation">
                            <Stack.Screen
                                name="Invitation"
                                component={InvitationScreen}
                                options={{ headerShown: true, title: "Invitation" }}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/Invitation",
    component: StoryComponent,
    argTypes: {
        target: {
            control: "radio",
            options: [InvitationTarget.Worker, InvitationTarget.Client],
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = { args: { target: InvitationTarget.Worker } };

/** Colleague invitation: accepting creates an employer profile on another screen. */
export const Colleague: Story = { args: { target: InvitationTarget.Client } };
